/**
 * The Gemeinden overview (BRIEF product scope 4, first bullet).
 *
 * The question this page answers in one glance is "which Gemeinden can I trust,
 * and which are drafts". So the drafts are NOT sorted to the bottom of one long
 * list where they read as "nearly done" — they stand in their own section,
 * first, under their own heading, each in the dashed indicative treatment. A
 * reader who only reads the two headings has the answer.
 *
 * Everything else on a card is the evidence behind that answer: the verified /
 * open counts for zones and rules, and the source documents with the versions
 * actually on record. A pack claiming "verified" without a source document
 * would be a claim about itself; the sources are what make it checkable.
 */
import { FileText, Plus, ShieldCheck } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useI18n } from '@/i18n'
import { Link, buildPath, navigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

import { CoverageMeter } from './CoverageMeter'
import { DraftBadge, OpenCountBadge } from './DraftBadge'
import { ShadowedNote } from './ShadowedNote'
import { isDraft, openCount, packIdOf, splitPackId } from './rulebase'
import type { DraftPackRef, GemeindePack, WorkspaceRef } from '@/api/types'

export function GemeindenOverview({ workspace }: { workspace: WorkspaceRef }) {
  const { t } = useI18n()
  const packs = useAsync<GemeindePack[]>(
    (signal) => api.gemeinden(workspace.id, signal),
    [workspace.id],
  )

  // `GET /gemeinden` answers with the packs that a run would actually use, so a
  // draft shadowed by a committed pack is deliberately absent from it. That is
  // the right answer to "what runs" and the wrong answer to "where did my
  // ingestion go" — so the workspace's own list fills that second question in,
  // in a section of its own that says the drafts are not used.
  const workspacePacks = useAsync<DraftPackRef[]>(
    (signal) => api.draftPacks(workspace.id, signal),
    [workspace.id],
  )

  const all = packs.data ?? []
  const drafts = all.filter(isDraft)
  const verified = all.filter((pack) => !isDraft(pack))
  const shadowed = (workspacePacks.data ?? []).filter((ref) => ref.shadowed)

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="font-display text-[32px] leading-tight font-semibold">
          {t('rulebase.title')}
        </h1>
        <p className="max-w-[70ch] text-[15px] text-muted">{t('rulebase.lead')}</p>
        <p className="text-[13px] text-muted-2" data-testid="rulebase-summary">
          {/* One draft is "1 Entwurf", not "1 Entwurf/Entwürfe" — a slash is
              a placeholder for a plural rule, not a plural rule. */}
          {t(drafts.length === 1 ? 'rulebase.summaryOne' : 'rulebase.summary', {
            total: all.length,
            verified: verified.length,
            drafts: drafts.length,
          })}
        </p>
        <Button
          className="self-start"
          onClick={() => navigate(buildPath('rulebaseIngest', { workspaceId: workspace.id }))}
        >
          <Icon icon={Plus} />
          {t('rulebase.ingestCta')}
        </Button>
        <p className="text-[12px] text-muted">{t('rulebase.noPackFor')}</p>
      </section>

      {packs.loading ? <p className="text-[13px] text-muted">{t('common.loading')}</p> : null}
      {packs.error ? (
        <ErrorNote message={packs.error} onRetry={packs.reload} retryLabel={t('common.retry')} />
      ) : null}
      {!packs.loading && !packs.error && all.length === 0 ? (
        <p className="text-[13px] text-muted">{t('rulebase.empty')}</p>
      ) : null}

      {/* Drafts first and separately. Mixing them into one list sorted by
          coverage is the reading this section exists to prevent. */}
      {drafts.length > 0 ? (
        <PackSection
          id="rulebase-drafts"
          title={t('rulebase.draftSectionTitle')}
          packs={drafts}
          workspaceId={workspace.id}
        />
      ) : null}

      {verified.length > 0 ? (
        <PackSection
          id="rulebase-verified"
          title={t('rulebase.verifiedSectionTitle')}
          packs={verified}
          workspaceId={workspace.id}
        />
      ) : null}

      {shadowed.length > 0 ? (
        <section
          aria-labelledby="rulebase-shadowed"
          className="flex flex-col gap-4"
          data-testid="rulebase-shadowed"
        >
          <h2 id="rulebase-shadowed" className="font-display text-[21px] font-semibold">
            {t('rulebase.shadowedSectionTitle')}
          </h2>
          <ShadowedNote conflicts={[]} />
          <ul className="flex flex-wrap gap-x-4 gap-y-2">
            {shadowed.map((ref) => (
              <li key={ref.pack_id} className="flex items-center gap-2">
                <Link
                  to={buildPath('rulebasePack', {
                    workspaceId: workspace.id,
                    kanton: splitPackId(ref.pack_id).kanton,
                    gemeinde: splitPackId(ref.pack_id).gemeinde,
                  })}
                  className="text-[13px] text-ink underline underline-offset-2"
                >
                  {ref.gemeinde}
                </Link>
                <DraftBadge />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

function PackSection({
  id,
  title,
  packs,
  workspaceId,
}: {
  id: string
  title: string
  packs: GemeindePack[]
  workspaceId: string
}) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-4" data-testid={id}>
      <h2 id={id} className="font-display text-[21px] font-semibold">
        {title}
      </h2>
      <ul className="grid gap-4 md:grid-cols-2">
        {packs.map((pack) => (
          <li key={packIdOf(pack)}>
            <PackCard pack={pack} workspaceId={workspaceId} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function PackCard({ pack, workspaceId }: { pack: GemeindePack; workspaceId: string }) {
  const { t } = useI18n()
  const draft = isDraft(pack)
  const packId = packIdOf(pack)
  const { kanton, gemeinde } = splitPackId(packId)
  const href = buildPath('rulebasePack', { workspaceId, kanton, gemeinde })
  const statusNote = pack.status_note_de ?? null
  const ingestedAt = pack.ingested_at ?? null
  // Zones AND rules: both are work the Gemeinde still owes, and a card that
  // counted only one of them would be the same half-truth in a new place.
  const openTotal = openCount(pack.zones) + openCount(pack.rules_effective)

  return (
    <Card
      // The indicative treatment, identical to the report's: dashed border,
      // grey ground, not one signal token anywhere near it.
      className={draft ? 'h-full border-dashed border-border-strong bg-surface' : 'h-full'}
      data-testid="pack-card"
      data-pack={packId}
      data-draft={draft}
    >
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>
            {pack.gemeinde} <span className="text-muted-2">({pack.kanton})</span>
          </CardTitle>
          {/* The badge carries its own fraction, and the remainder stands next
              to it. A bare «verifiziert» over a coverage line reading «4 offen»
              was a card contradicting itself — the reader had to decide which
              half to believe, and nothing on the card said which. */}
          {draft ? (
            <DraftBadge />
          ) : (
            <span className="flex flex-wrap items-center gap-2">
              <Badge variant="info" data-testid="verified-badge">
                <Icon icon={ShieldCheck} />
                {t('rulebase.verifiedBadgeCount', {
                  verified: pack.rules_effective.verified,
                  total: pack.rules_effective.total ?? 0,
                })}
              </Badge>
              {openTotal > 0 ? <OpenCountBadge count={openTotal} /> : null}
            </span>
          )}
        </div>
        <CardDescription>
          <code className="num">{packId}</code>
          {pack.regime ? (
            <>
              {' '}
              · {t('coverage.regime')}: <span className="num">{pack.regime}</span>
            </>
          ) : null}
          {ingestedAt ? (
            <>
              {' '}
              · {t('rulebase.ingestedAt', { date: formatDate(ingestedAt) })}
            </>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* The backend's own sentence, not one written here — one wording for
            this state across the whole product. */}
        {statusNote ? (
          <p className="text-[13px] text-muted" data-testid="pack-status-note">
            {statusNote}
          </p>
        ) : null}

        <CoverageMeter label={t('rulebase.zones')} counts={pack.zones} testId="coverage-zones" />
        <CoverageMeter
          label={t('rulebase.rules')}
          counts={pack.rules_effective}
          testId="coverage-rules"
        />

        <div>
          <p className="text-[12px] font-medium text-muted-2">{t('rulebase.sources')}</p>
          {(pack.sources ?? []).length === 0 ? (
            <p className="mt-1 text-[12px] text-muted">{t('rulebase.noSources')}</p>
          ) : (
            <ul className="mt-1 flex flex-col gap-1">
              {(pack.sources ?? []).map((source) => {
                const pageCount = source.page_count ?? null
                return (
                  <li
                    key={source.key}
                    className="flex items-start gap-2 text-[12px] leading-snug text-muted"
                  >
                    <Icon icon={FileText} className="mt-0.5 size-3.5" />
                    <span>
                      <span className="num text-muted-2">{source.key}</span>
                      {source.doc ? <> — {source.doc}</> : null}
                      {source.version ? (
                        <span className="text-muted-2"> · {source.version}</span>
                      ) : null}
                      {pageCount ? (
                        <span className="text-muted-2">
                          {' '}
                          · {t('rulebase.pages', { count: pageCount })}
                        </span>
                      ) : null}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* Both kinds of pack have a page now (features/rulebase/PackScreen):
            a draft opens the verification queue, a committed pack opens its
            read-only detail. The link used to be draft-only because the route
            answered «Entwurfs-Pack nicht gefunden» for the committed ones —
            a dead end on the four packs everyone opens first. */}
        <Link
          to={href}
          className="inline-flex w-fit items-center gap-2 rounded-sm border border-border-strong bg-white px-3 py-1.5 text-[13px] font-medium text-ink hover:bg-surface"
        >
          {draft ? t('rulebase.openPack') : t('rulebase.openPackVerified')}
        </Link>
      </CardContent>
    </Card>
  )
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat('de-CH', { dateStyle: 'medium' }).format(date)
}
